import React, { FC } from "react";
import styled from "styled-components";

import { ONGOING_STATES } from "../constants";
import { useEmbedParametersContext } from "../contexts/EmbedParametersContext";
import { State } from "../enums";
import { isYleEmbed } from "../helpers/commonUtils";
import { localizedText } from "../helpers/previewHelpers";
import i18n from "../service/i18n";
import { usePlayerStore } from "../store/usePlayerStore";
import {
  PlayerEmbedParams,
  PlayerRefreshEvent,
  PlayerUpdateEvent,
  SetPlayerApiFn
} from "../types/common";
import { Media } from "../types/player-api";

import { ErrorOverlay } from "./Error/ErrorOverlay/ErrorOverlay";
import { NonPlayableVideo } from "./NonPlayable/NonPlayableVideo";
import { PreviewAspectRatio } from "./AspectRatio";
import ContentRatingIcons from "./ContentRatingIcons";
import { PreviewImage } from "./PreviewImage";
import ReturnToYleDomain from "./ReturnToYleDomain";
import { YlePlayer } from "./YlePlayer";

const ScreenreaderOnlyTitle = styled.span`
  border: 0;
  clip-path: inset(50%);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`;

const PreviewOverlay = styled.div`
  background-image: linear-gradient(
    0deg,
    rgb(0 0 0 / 80%) 0%,
    rgb(0 0 0 / 0%) 50%
  );
  inset: 0;
  position: absolute;
`;

const parseAccessibleDescription = (
  embedParams: PlayerEmbedParams,
  mediaData: Media | undefined,
  isOngoing: boolean
) => {
  const { backgroundVideo, language, title } = embedParams;

  const videoPlayerType = backgroundVideo
    ? i18n("video_player_illustrational", { language })
    : i18n("video_player", { language });

  if (title && isOngoing) {
    return videoPlayerType; // The title will be shown on video
  }

  const accessibleDescription =
    localizedText(mediaData?.title, language) ||
    localizedText(mediaData?.description, language) ||
    "";

  return `${accessibleDescription}${accessibleDescription && ", "}${videoPlayerType}`;
};

interface Props {
  refreshComponent: PlayerRefreshEvent;
  setPlayerApi: SetPlayerApiFn;
  setPlayerApiSimple: () => void;
  state: State;
  update: PlayerUpdateEvent;
}

export const PreviewVideo: FC<Props> = ({
  refreshComponent,
  setPlayerApi,
  setPlayerApiSimple,
  state,
  update
}) => {
  const { useStoreState } = usePlayerStore();
  const isPlaybackStarted = useStoreState.isPlaybackStarted();
  const mediaData = useStoreState.media();

  const embedParams = useEmbedParametersContext();
  const { autoplay, previewImage } = embedParams;

  const isOngoing = ONGOING_STATES.includes(state) && !!mediaData;
  const isInPreviewState = isOngoing && !autoplay && !isPlaybackStarted;
  const showContentRatingIcons =
    !autoplay && !isPlaybackStarted && state === State.ONGOING_ONDEMAND;
  const ariaLabel = parseAccessibleDescription(
    embedParams,
    mediaData,
    isOngoing
  );

  return (
    <>
      <ScreenreaderOnlyTitle>{ariaLabel}</ScreenreaderOnlyTitle>
      <PreviewAspectRatio>
        {previewImage && (isInPreviewState || !isOngoing) && <PreviewImage />}
        {isOngoing && (
          <>
            <ErrorOverlay />
            {showContentRatingIcons && <ContentRatingIcons />}
            {!isYleEmbed() && isInPreviewState && (
              <PreviewOverlay>
                <ReturnToYleDomain preview={true} />
              </PreviewOverlay>
            )}
            <YlePlayer
              setPlayerApi={setPlayerApi}
              update={update}
              refreshComponent={refreshComponent}
            />
          </>
        )}
        {!isOngoing && (
          <NonPlayableVideo
            refreshComponent={refreshComponent}
            setPlayerApiSimple={setPlayerApiSimple}
            state={state}
          />
        )}
      </PreviewAspectRatio>
    </>
  );
};
